import React, { useState } from 'react';
import { EntryChild, EntryRow, Mode } from './types';
import { Entity } from '@ecosystem/newron-core/types';
import { inputs, elements, actions } from '@ecosystem/newron-design-system';
import { Market } from '../types';

const { TextInput } = inputs;
const { Icon } = elements;
const { Button, IconButton } = actions;

function search(item: Entity, term: string): boolean {
  const terms = term.trim().toLocaleLowerCase().split(' ');
  const parts = item.label.trim().toLocaleLowerCase().split(' ');

  return terms.some(t => parts.some(part => part.includes(t)));
}

type Props = {
  mode: Mode;
  markets: Market[];
  cells: Entity[];
  onAddCells: (cellsToAdd: Entity[]) => void;
  indicateSubscriptions: boolean;
};
function MarketsSelector({ mode, cells, markets, onAddCells, indicateSubscriptions }: Props) {
  const [term, setTerm] = useState<string | undefined>();

  const selectedCells = cells.map(c => c.value);
  const parentCodes = new Set<string>();

  let rows: EntryRow[] = [];
  markets.forEach(market => {
    if (!selectedCells.includes(market.value)) {
      const parent: Entity = {
        value: mode === 'byCountry' ? market.countryCode : market.productCode,
        label: mode === 'byCountry' ? market.countryName : market.productName,
      };
      const child: EntryChild = {
        value: mode === 'byCountry' ? market.productCode : market.countryCode,
        label: mode === 'byCountry' ? market.productName : market.countryName,
        subscriptions: market.subscriptions,
      };

      if (!parentCodes.has(parent.value)) {
        rows.push({
          parent,
          children: [child],
        });
        parentCodes.add(parent.value);
      } else {
        const row = rows.find(r => r.parent.value === parent.value);
        if (row) {
          row.children.push(child);
          row.children.sort((a, b) => a.label.localeCompare(b.label));
        }
      }
    }
  });

  rows.sort((a, b) => a.parent.label.localeCompare(b.parent.label));

  if (term && term.length) {
    // search parent first
    const filteredRows: EntryRow[] = rows.filter(row => search(row.parent, term));
    // now children of any non matching rows
    const matched = filteredRows.map(r => r.parent.value);
    rows
      .filter(r => !matched.includes(r.parent.value))
      .forEach(row => {
        const matchingChildren = row.children.filter(child => search(child, term));
        if (matchingChildren.length) {
          const filteredRow: EntryRow = {
            parent: row.parent,
            children: matchingChildren,
          };
          filteredRows.push(filteredRow);
        }
      });
    filteredRows.sort((a, b) => a.parent.label.localeCompare(b.parent.label));
    rows = filteredRows;
  }

  const handleAddItem = (parentCode: string, childCode: string) => {
    const cell = mode === 'byCountry' ? `${parentCode}-${childCode}` : `${childCode}-${parentCode}`;
    const market = markets.find(m => m.value === cell);
    if (market) {
      onAddCells([
        {
          value: market.value,
          label: market.label,
        },
      ]);
    }
  };

  const handleAddRow = (row: EntryRow) => {
    const cells = row.children.map(child => {
      const cell = mode === 'byCountry' ? `${row.parent.value}-${child.value}` : `${child.value}-${row.parent.value}`;
      return cell;
    });
    const marketsToAdd = markets.filter(m => cells.includes(m.value));
    if (marketsToAdd.length) {
      onAddCells(
        marketsToAdd.map(m => ({
          value: m.value,
          label: m.label,
        }))
      );
    }
  };

  const handleAddAll = () => {
    const cells = rows.flatMap(row =>
      row.children.map(child => {
        const cell = mode === 'byCountry' ? `${row.parent.value}-${child.value}` : `${child.value}-${row.parent.value}`;
        return cell;
      })
    );

    const marketsToAdd = markets.filter(m => cells.includes(m.value));
    if (marketsToAdd.length) {
      onAddCells(
        marketsToAdd.map(m => ({
          value: m.value,
          label: m.label,
        }))
      );
    }
  };

  let noRowsMsg = '';
  if (!rows.length) {
    noRowsMsg = term ? 'No markets match your search' : 'No markets left to add';
  }

  return (
    <div className="relative flex-shrink-0 w-96 flex flex-column">
      <div className="min-h-14 h-14 flex-shrink-0 flex items-center justify-between border-b border-light px-4 gap-2">
        <div className="flex-grow" style={{ marginTop: '-4px' }}>
          <TextInput value={term} onChange={val => setTerm(val)} placeholder="Search for markets to add">
            <TextInput.Prefix>
              <Icon>
                <Icon.icons.Search />
              </Icon>
            </TextInput.Prefix>
            {term && (
              <TextInput.Suffix>
                <IconButton size="sm" onClick={() => setTerm(undefined)}>
                  <IconButton.icons.Close />
                </IconButton>
              </TextInput.Suffix>
            )}
          </TextInput>
        </div>
        <Button mode="simple" onClick={handleAddAll}>
          Add all
        </Button>
      </div>
      <div className="pb-4 flex-grow overflow-y-auto overscroll-contain">
        {!rows.length && <div className="text-lighter text-center py-10 px-20">{noRowsMsg}</div>}
        {rows.map((row, idx) => (
          <div
            className={`flex flex-row py-2 ${idx !== rows.length - 1 ? 'border-b border-light' : ''}`}
            key={row.parent.value}
          >
            <div className="pl-4 pr-1 py-1 w-28 min-w-28 flex-shrink-0 font-bold text-xs">{row.parent.label}</div>
            <div className="flex-grow flex flex-row gap-x-2 gap-y-1 flex-wrap">
              {row.children.map(child => (
                <button
                  onClick={() => handleAddItem(row.parent.value, child.value)}
                  className="text-left text-xs flex flex-row gap-1 items-center px-2 h-8 rounded transition-colors border border-default bg-white hover:bg-brand-lightest"
                  key={child.value}
                >
                  <span className="nowrap overflow-ellipses max-w-40">{child.label}</span>
                  {indicateSubscriptions && <span className="text-lighter">({child.subscriptions})</span>}
                  <Icon size="sm" color="brand">
                    <Icon.icons.Add />
                  </Icon>
                </button>
              ))}
            </div>
            <div className="pr-4 flex-shrink-0">
              <IconButton onClick={() => handleAddRow(row)}>
                <IconButton.icons.Add />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { MarketsSelector };
